import { Box, Container, Grid, Typography } from "@mui/material";
import { spacing } from "~/pages/reports/utils/reportsConstants";
import { useCveOrAdvisoryReport } from "./hooks/useCveOrAdvisoryReport";
import { Stats } from "~/components/DetailsPage/components";
import {
  CveSources,
  CvssRiskContent,
  CvssRiskProvider,
  CvssVector,
  EpssRiskContent,
  EpssRiskProvider,
  Sources,
} from "~/components/vulnerabilities";
import { ReportsPrintScoreBlock } from "../components";
import { DetectedBy } from "~/components/vulnerabilities/DetectedBy/DetectedBy";
import { AssetReportRiskFactors } from "../AssetReport/components/AssetReportRiskFactors";
import { CveOrAdvisoryReportExposedAssets } from "./components/CveOrAdvisoryReportExposedAssets";
import { CveOrAdvisoryReportContentDetails } from "./components/CveOrAdvisoryReportContentDetails";
import { Markdown } from "~/components/markdown";
import { ReportsSectionHeading } from "~/pages/reports/components/ReportsSectionHeading";

export function CveOrAdvisoryReport() {
  const { data, aggregateNodeData } = useCveOrAdvisoryReport();

  const hasAdvisorySources =
    Boolean(data.sourceLinks.length > 0) && data.type === "advisory";
  const hasCveSources = data.cveSources && data.type === "cve";
  const hasSources = hasAdvisorySources || hasCveSources;

  return (
    <Container sx={{ p: 4 }}>
      <Grid container>
        <Grid size={12} sx={{ mb: spacing[50] }}>
          <Typography variant="h4" fontWeight={700}>
            {data.title}
          </Typography>
        </Grid>
        <Grid size={12}>
          <ReportsSectionHeading heading="Summary" disableUpperPadding />
        </Grid>

        <Stats id="detail-content-stats" stats={data.stats} />
        <Box sx={{ position: "relative", width: "100%" }}>
          {aggregateNodeData?.riskValue != null &&
            aggregateNodeData?.rating && (
              <Box
                sx={{
                  float: "right",
                  ml: 3,
                  mb: 1,
                  mt: 3.75,
                }}
              >
                <ReportsPrintScoreBlock
                  mappings={data.mappings}
                  ratingValues={{
                    value: aggregateNodeData.riskValue,
                    rating: aggregateNodeData.rating,
                  }}
                />
              </Box>
            )}

          {data.summary && (
            <Markdown
              source={data.summary}
              externalLinksInNew
              copyButton={false}
              sx={{
                my: spacing[25],
                "& p": {
                  fontSize: 14,
                  fontFamily: "Roboto",
                  lineHeight: "1.43",
                  textAlign: "justify",
                },
              }}
            />
          )}
        </Box>

        <Grid size={12} sx={{ mb: spacing[25] }}>
          <DetectedBy
            detectedBy={data?.detectedBy}
            widthOverride={{ xs: 12 }}
          />
        </Grid>
        {hasSources && (
          <Grid size={12}>
            {hasAdvisorySources && (
              <Sources id="advisory-sources" links={data.sourceLinks} />
            )}
            {data.cveSources && (
              <CveSources id="cve-sources" {...{ ...data.cveSources }} />
            )}
          </Grid>
        )}
        <Grid size={12} sx={{ mb: spacing[50] }}>
          <AssetReportRiskFactors
            dataType={data.type}
            riskFactors={data.riskFactorsWithDocs}
          />
        </Grid>

        {data.cvssScore?.value && (
          <Grid size={12} sx={{ mb: spacing[50] }}>
            <CvssRiskProvider vector={data.cvssScore.vector} isPrintView>
              <ReportsSectionHeading heading="CVSS v3 score" sx={{ pb: 0 }} />
              <Box sx={{ pt: 1, pb: spacing[25] }}>
                <CvssVector />
              </Box>
              <CvssRiskContent />
            </CvssRiskProvider>
          </Grid>
        )}

        {data.epssScore && data.cvssScore && (
          <Grid size={12} sx={{ mb: spacing[50] }}>
            <ReportsSectionHeading heading="EPSS score" />
            <EpssRiskProvider
              probability={data.epssScore.probability}
              percentile={data.epssScore.percentile}
              cvssScore={data.cvssScore}
              printView
            >
              <Box sx={{ pt: 3 }}>
                <EpssRiskContent />
              </Box>
            </EpssRiskProvider>
          </Grid>
        )}

        {data.type === "advisory" && (
          <Grid size={12} sx={{ mb: spacing[50] }}>
            <CveOrAdvisoryReportContentDetails
              vulnerabilities={data.vulnerabilities}
            />
          </Grid>
        )}

        {/* Where do I source these */}
        {/* {data.type === "cve" && (
          <Grid size={12} sx={{ mb: spacing[50] }}>
            <RelatedAdvisories relatedAdvisories={data.relatedAdvisories} />
          </Grid>
        )} */}

        <Grid size={12} sx={{ mb: spacing[50] }}>
          <CveOrAdvisoryReportExposedAssets
            affectedAssets={data.affectedAssets}
          />
        </Grid>
      </Grid>
    </Container>
  );
}
